import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface TestParseParams {
  file: File;
  supplierId: string;
  templateId: string;
  parserType: 'ai' | 'rule' | 'hybrid';
  ocrEngine: 'tesseract' | 'google-vision';
  confidenceThreshold: number;
  customRules: any;
}

export const useParserSandbox = (supplierId?: string) => {
  // Test parse - use multipart/form-data directly
  const testParse = useMutation({
    mutationFn: async (params: TestParseParams) => {
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('file', params.file);
      formData.append('SupplierName', params.supplierId);

      // Call edge function with FormData
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('parser-sandbox', {
        body: formData,
      });

      if (error) throw error;
      return data;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Parser test failed');
    }
  });

  // Get unparsed bills for dropdown
  const { data: unparsedBills } = useQuery({
    queryKey: ['unparsed-bills-for-sandbox'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parser_jobs')
        .select('id, file_url, supplier_id, created_at')
        .eq('progress_status', 'needs_review')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      return data;
    }
  });

  // Get suppliers for dropdown
  const { data: suppliers } = useQuery({
    queryKey: ['suppliers-for-sandbox'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name')
        .order('name');

      if (error) throw error;
      return data;
    }
  });

  // Get parser templates by supplier (mock data for now)
  const templates = supplierId ? [
    { id: '1', name: 'Standard Template', version: '1.0', updated_at: new Date().toISOString() },
    { id: '2', name: 'Advanced Template', version: '2.0', updated_at: new Date().toISOString() },
  ] : [];

  return { testParse, unparsedBills, templates, suppliers };
};
